import "./FilterTags.scss"

import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { throttle } from "lodash"

import { IAppliedFilter } from "@/types"
import { FilterCategory, FilterCategoryToLabelMap } from "config/constants"
import Tag, { ITagProps, TagTheme } from "components/Tag/Tag"
import DynamicOverflow from "components/DynamicOverflow/DynamicOverflow"
import { InfoDropdown } from "components/InfoDropdown"
import TagMore from "components/Tag/TagMore"
import { getRandomNumber } from "utils/utils"

interface IFilterTags {
    filters: IAppliedFilter[]
    onTagRemove?(filter: IAppliedFilter): void
    justify?: "start" | "end"
}

const FilterTags: FunctionComponent<React.PropsWithChildren<IFilterTags>> = ({
    filters,
    onTagRemove,
    justify = "start",
}) => {
    const { t } = useTranslation()
    const [key, setKey] = useState<number>(getRandomNumber())




    const tags = useMemo<ITagProps[]>(() => {
        const validFilters = filters.filter(filter => Object.values(FilterCategory).includes(filter.category))

        const availabilityFilters = validFilters.filter(f => f.category === FilterCategory.availability)
        const nonAvailabilityFilters = validFilters.filter(f => f.category !== FilterCategory.availability)

        function buildLabel(filterCategory: FilterCategory, baseLabel: string) {
            if (filterCategory === FilterCategory.startsWithinDays
                || filterCategory === FilterCategory.endsWithinDays
                || filterCategory === FilterCategory.disposalDateDiffersCreationPeriod
                || filterCategory === FilterCategory.disposalDateDiffersInvoicePeriod
                || filterCategory === FilterCategory.invoiceDateDiffersCreationPeriod
                || filterCategory === FilterCategory.invoicePendingMoreThanDays
                || filterCategory === FilterCategory.missingDepreciationRate
                || filterCategory === FilterCategory.lastInvoice
                || filterCategory === FilterCategory.invoiceBeforeShipping
                || filterCategory === FilterCategory.invoiceBlocked) {
                return baseLabel
            }
            return `${baseLabel} (${t(FilterCategoryToLabelMap[filterCategory])})`
        }

        const result: ITagProps[] = []

        // Group availability filters by their (translated) label so duplicate labels never create multiple badges.
        if (availabilityFilters.length > 0) {
            const isRemovable = typeof onTagRemove === 'function'
            const clusters = availabilityFilters.reduce<Record<string, typeof availabilityFilters>>((acc, f) => {
                // Normalize label grouping: any availability that maps to the same translated base (Available) should collapse
                // We rely on useFilter already translating values (available, notAvailable, borrowExpired => Available label)
                // but fallback to manual mapping if label missing.
                let base = (f.label ?? '').toString()
                if (!base) {
                    const raw = (f.value ?? '').toString()
                    if (raw === 'available' || raw === 'notAvailable' || raw === 'borrowedExpired') {
                        base = t('availabilityUi.quickFilters.available')
                    }
                }
                const clusterKey = base
                if (!acc[clusterKey]) acc[clusterKey] = []
                acc[clusterKey].push(f)
                return acc
            }, {})

            Object.entries(clusters).forEach(([labelBase, clusterFilters]) => {
                result.push({
                    label: buildLabel(FilterCategory.availability, labelBase),
                    onClick: isRemovable ? () => {
                        clusterFilters.forEach(f => onTagRemove && onTagRemove(f))
                    } : undefined,
                    crossIcon: isRemovable,
                })
            })
        }
        else if (availabilityFilters.length === 1) {
            const f = availabilityFilters[0]
            const base = f.label ?? f.value ?? ""
            const isRem = typeof onTagRemove === 'function'
            result.push({
                label: buildLabel(f.category, base),
                onClick: isRem ? () => onTagRemove && onTagRemove(f) : undefined,
                crossIcon: isRem,
            })
        }

        // Add the rest
        nonAvailabilityFilters.forEach(filter => {
            const base = filter.label ?? filter.value ?? ""
            const isRemovable = typeof onTagRemove === 'function'
            result.push({
                label: buildLabel(filter.category, base),
                onClick: isRemovable ? () => onTagRemove && onTagRemove(filter) : undefined,
                crossIcon: isRemovable,
            })
        })

        // Preserve original order roughly: sort by first appearance index of any underlying filter
        const originalOrderMap = new Map<ITagProps, number>()
        result.forEach(tag => {
            // Extract base label without suffix to map back to an original filter
            const baseLabel = tag.label.split(' (')[0]
            const idx = validFilters.findIndex(f => (f.label ?? f.value ?? "") === baseLabel)
            originalOrderMap.set(tag, idx === -1 ? 9999 : idx)
        })
        return result.sort((a, b) => (originalOrderMap.get(a)! - originalOrderMap.get(b)!))
    }, [filters, onTagRemove, t])

    const handleResize = throttle(refreshKey, 200)

    useEffect(refreshKey, [filters])



    function refreshKey() {
        setKey(getRandomNumber())
    }

    const resizeObserverRef = useRef<ResizeObserver>(new ResizeObserver(handleResize))
    const resizeContainerRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        const containerNode = resizeContainerRef.current
        const observer = resizeObserverRef.current
        if (!containerNode) return
        observer.observe(containerNode)
        return () => {
            observer.disconnect()
        }
    }, [resizeContainerRef])


    function getMoreLabel(isExpanded: boolean, overflowElements: React.JSX.Element[]) {
        return <TagMore
            label={t('common.xMore', { count: overflowElements.length })}
            isExpanded={isExpanded}
        />
    }

    return (
        <div ref={resizeContainerRef}>
            <DynamicOverflow
                key={key}
                list={({ nodeRef }) => tags.map((tag, index) => (
                    <div ref={nodeRef}
                        key={`tag-${index}`}
                        className="filter-tags__item">
                        <Tag {...tag} />
                    </div>
                ))}>
                {
                    ({ visibleElements, overflowElements, containerRef, moreNodeRef }) => {
                        return (
                            <div
                                ref={containerRef}
                                className={`filter-tags filter-tags--justify-${justify}`}>
                                {overflowElements.length > 0 &&
                                    <div className="filter-tags__item filter-tags__item--more"
                                        ref={moreNodeRef}
                                        key="tag-overflow">
                                        <InfoDropdown
                                            label={isExpanded => getMoreLabel(isExpanded, overflowElements)}
                                            placement="bottom-start"
                                            content={
                                                <div className="filter-tags__more">
                                                    {overflowElements.map((el, index) => {
                                                        return <Tag key={`tag-${index}`} {...el.props.children.props} theme={TagTheme.transparent} />
                                                    })}
                                                </div>
                                            } />
                                    </div>
                                }
                                {visibleElements}
                            </div>
                        )
                    }
                }
            </DynamicOverflow>
        </div>
    )
}

export default FilterTags
